// AIDashboardLanding.tsx
import React, { useState, useEffect, FC } from 'react';
import '../css/landing-page.css';
import { useNavigate } from 'react-router-dom';
// Type definitions
interface AnimatedStatProps {
  number: string;
  label: string;
  delay?: number;
}

interface FeatureCardData {
  icon: string;
  title: string;
  description: string;
}

interface StatData {
  number: string;
  label: string;
  delay: number;
}

interface FooterSection {
  title: string;
  links: string[];
}

const AIDashboardLanding: FC = () => {
  const [isScrolled, setIsScrolled] = useState<boolean>(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);
  const [statsAnimated, setStatsAnimated] = useState<boolean>(false);
    const navigate = useNavigate();
   const handleClick = () => {
    const isLoggedIn = localStorage.getItem("userToken"); 

    if (isLoggedIn) {
      navigate("/dashboard"); 
    } else {
      navigate("/login")
    }
  };

  useEffect(() => {
    const handleScroll = (): void => {
      setIsScrolled(window.scrollY > 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string): void => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  const AnimatedStat: FC<AnimatedStatProps> = ({ number, label, delay = 0 }) => {
    const [currentNumber, setCurrentNumber] = useState<number>(0);

    useEffect(() => {
      if (statsAnimated) {
        const targetNumber = parseInt(number.replace(/[^\d]/g, ''));
        const duration = 2000;
        const steps = 60;
        const increment = targetNumber / steps;
        let current = 0;

        const timer = setInterval(() => {
          current += increment;
          if (current >= targetNumber) {
            setCurrentNumber(targetNumber);
            clearInterval(timer);
          } else {
            setCurrentNumber(Math.floor(current));
          }
        }, duration / steps);

        return () => clearInterval(timer);
      }
    }, [statsAnimated, number]);

    const formatNumber = (num: number): string => {
      if (number.includes('M+')) return `${Math.floor(num / 1000000)}M+`;
      if (number.includes('K+')) return `${Math.floor(num / 1000)}K+`;
      if (number.includes('%')) return `${(num / 100).toFixed(1)}%`;
      if (number.includes('/')) return number;
      return num.toLocaleString();
    };

    return (
      <div className="stat-item" style={{ animationDelay: `${delay}ms` }}>
        <div className="stat-number">
          {statsAnimated ? formatNumber(currentNumber) : number}
        </div>
        <div className="stat-label">{label}</div>
      </div>
    );
  };

  const StatsSection: FC = () => {
    useEffect(() => {
      const observer = new IntersectionObserver(
        (entries: IntersectionObserverEntry[]) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setStatsAnimated(true);
            }
          });
        },
        { threshold: 0.5 }
      );

      const statsSection = document.getElementById('stats');
      if (statsSection) {
        observer.observe(statsSection);
      }

      return () => observer.disconnect();
    }, []);

    const statsData: StatData[] = [
      { number: "10M+", label: "Data Points Processed", delay: 0 },
      { number: "99.9%", label: "Uptime Reliability", delay: 200 },
      { number: "50K+", label: "Active Users", delay: 400 },
      { number: "24/7", label: "Support Available", delay: 600 }
    ];

    return (
      <section className="stats" id="stats">
        <div className="container">
          <div className="stats-grid">
            {statsData.map((stat, index) => (
              <AnimatedStat
                key={index}
                number={stat.number}
                label={stat.label}
                delay={stat.delay}
              />
            ))}
          </div>
        </div>
      </section>
    );
  };

  const featuresData: FeatureCardData[] = [
    {
      icon: "🧠",
      title: "Smart Analytics",
      description: "Advanced machine learning algorithms analyze your data patterns and provide actionable insights in real-time."
    },
    {
      icon: "📊",
      title: "Interactive Dashboards",
      description: "Create stunning, interactive visualizations that adapt to your data and provide deep insights at a glance."
    },
    {
      icon: "⚡",
      title: "Real-time Processing",
      description: "Process and analyze large datasets in real-time with our high-performance AI engine."
    },
    {
      icon: "🎯",
      title: "Predictive Modeling",
      description: "Forecast trends and make predictions using our advanced AI models trained on your specific data."
    },
    {
      icon: "🔐",
      title: "Secure & Private",
      description: "Enterprise-grade security ensures your data remains protected with end-to-end encryption."
    },
    {
      icon: "🌐",
      title: "Cloud Integration",
      description: "Seamlessly integrate with popular cloud platforms and access your dashboard from anywhere."
    }
  ];

  const footerData: FooterSection[] = [
    {
      title: "Product",
      links: ["Features", "Dashboard", "Pricing", "API"]
    },
    {
      title: "Company",
      links: ["About Us", "Careers", "Press", "Contact"]
    },
    {
      title: "Support",
      links: ["Help Center", "Documentation", "Community", "Status"]
    },
    {
      title: "Legal",
      links: ["Privacy Policy", "Terms of Service", "Cookie Policy", "GDPR"]
    }
  ];

  const navItems = [
    { name: "Dashboard", href: "admin/dashboard" },
    { name: "Users", href: "admin/users" },
    { name: "Entities", href: "admin/entities" }
  ];


  const handleNavClick = (item: string): void => {
    const sectionId = item.toLowerCase().replace(' ', '-');
    scrollToSection(sectionId === 'home' ? 'home' : sectionId);
  };

  const handleFooterLinkClick = (link: string): void => {
    const sectionId = link.toLowerCase().replace(' ', '-');
    if (sectionId === 'features' || sectionId === 'dashboard' || sectionId === 'contact') {
      scrollToSection(sectionId);
    }
  };

  return (
    <div className="ai-dashboard">
      {/* Header */}
      <header className={isScrolled ? 'scrolled' : ''}>
        <div className="container">
          <nav>
            <div className="logo" onClick={() => scrollToSection('home')}>
              Brilliant Assistant Admin
            </div>
            <div className="menu-desktop">
            <ul className={`nav-links ${mobileMenuOpen ? 'mobile-open' : ''}`}>
              {navItems.map((item, index) => (
                <li key={index}>
                  <a href={item.href} onClick={() => handleNavClick(item.name)}>
                    {item.name}
                  </a>
                </li>
              ))}
            </ul>


            <button className="cta-btn" onClick={handleClick}>
              Get Started
            </button>
            </div>
            <button 
              className="mobile-menu"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              ☰
            </button>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero" id="home">
        <div className="container">
          <div className="hero-content">
            <h1>Transform Your Data with <span className="gradient-text">AI Intelligence</span></h1>
            <p>Unlock the power of artificial intelligence with our cutting-edge dashboard. Visualize, analyze, and make data-driven decisions like never before.</p>
            <div className="hero-buttons">
              <button className="btn-primary" onClick={handleClick}>
                🚀 Launch Dashboard
              </button>
              <button className="btn-secondary" onClick={handleClick}>
                Learn More
              </button>
            </div>
          </div>
        </div>
        <div className="floating-elements">
          <div className="floating-element"></div>
          <div className="floating-element"></div>
          <div className="floating-element"></div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features" id="features">
        <div className="container">
          <h2>Powerful <span className="gradient-text">AI Features</span></h2>
          <div className="features-grid">
            {featuresData.map((feature, index) => (
              <div key={index} className="feature-card">
                <div className="feature-icon">{feature.icon}</div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <StatsSection />

      {/* CTA Section */}
      <section className="cta-section" id="dashboard">
        <div className="container">
          <h2>Ready to Transform Your Data?</h2>
          <p>Join thousands of companies already using our AI dashboard to make smarter decisions.</p>
          <button className="btn-primary" onClick={handleClick}>Start Your Free Trial</button>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact">
        <div className="container">
          {/* <div className="footer-content">
            {footerData.map((section, index) => (
              <div key={index} className="footer-section">
                <h3>{section.title}</h3>
                <ul>
                  {section.links.map((link, linkIndex) => (
                    <li key={linkIndex}>
                      <button onClick={() => handleFooterLinkClick(link)}>
                        {link}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div> */}
          <p>&copy; 2025 AI Dashboard. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default AIDashboardLanding;